import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { registerFontkit } from 'pdfmake-unicode'

pdfMake.vfs = pdfFonts.pdfMake.vfs
pdfMake.fontkit = registerFontkit

const CUSTOM_SELECT_REASON_ENGLISH = {
  SOLD: 'Sale/Purchase',
  REPAIR_AND_RETURN: 'Repair and Return',
  SAMPLE: 'Sample',
  PERSONAL_EFFECTS: 'Personal Effects',
}

const generateReportPDF = (data, t, webUserName) => {
  return new Promise((resolve, reject) => {
    const styles = {
      header: {
        fontSize: 16,
        bold: true,
        margin: [0, 0, 0, 15],
        color: '#4E5D6C',
        background: '#D9E1E8',
        fillColor: '#D9E1E8',
      },
      subheader: {
        fontSize: 14,
        bold: true,
        color: '#4E5D6C',
        fillColor: '#EBF0F2',
        margin: [0, 5, 0, 5],
      },
      content: {
        fontSize: 12,
        margin: [0, 2, 0, 2],
      },
      tableHeader: {
        bold: true,
        fontSize: 12,
        color: 'black',
        fillColor: '#D9E1E8',
        margin: [0, 5, 0, 5],
      },
      tableContent: {
        margin: [0, 2, 0, 2],
        fillColor: '#EBF0F2',
      },
      formLabel: {
        fillColor: '#D9E1E8',
        color: '#4E5D6C',
        bold: true,
        margin: [0, 5, 0, 2],
      },
      formField: {
        fillColor: '#FFFFFF',
        margin: [0, 2, 0, 2],
        border: [false, false, false, true],
        color: '#4E5D6C',
      },
      fieldName: {
        bold: true,
        color: '#000000',
        margin: [0, 2, 0, 2],
      },
    }

    const inventoryItems = data.customs.inventory.map(item => [
      item.description,
      item.category.commodity_taric_code,
      item.place.country_EN,
      item.quantity,
      `${item.price} €`,
      `${(item.quantity * parseFloat(item.price)).toFixed(2)} €`,
    ])

    const declarationText = data.customs.readed ? 'YES' : 'NO'

    // Calcular el peso total de los paquetes
    const totalWeight = data.shipment.packages.reduce(
      (total, pkg) => total + (parseFloat(pkg.weight) || 0),
      0,
    )

    const shippingCost = data.shipment.serviceType.basePrice

    const formattedShippingCost = `${parseFloat(shippingCost).toFixed(2)} €`

    const totalInventoryCost = data.customs.inventory.reduce((acc, item) => {
      return acc + item.quantity * parseFloat(item.price)
    }, 0)

    const totalCostIncludingShipping =
      totalInventoryCost + parseFloat(shippingCost)

    const formattedTotalCostIncludingShipping = `${totalCostIncludingShipping.toFixed(
      2,
    )} €`

    const dualUseDeclaration = {
      unbreakable: true,
      stack: [
        {
          text: [
            { text: '7. Declaration of Dual Use\n', style: 'header' },
            {
              text: 'I declare that the goods indicated in the invoice are not subject to Spanish Royal Decree 679/2014 and also it not included in the appendix of the Regulation (EU) 2021/821 of the European Parliament and of the Council of 20 May 2021, setting up a Union regime for the control of exports, brokering, technical assistance transit and transfer of dual-use items (recast).\n',
              style: 'fieldName',
            },
            { text: 'Decision: ' + declarationText, style: 'content' },
          ],
          margin: [0, 5, 0, 10],
        },
      ],
    }

    const citesDeclaration = {
      unbreakable: true,
      stack: [
        {
          text: [
            { text: '8. CITES Declaration\n', style: 'header' },
            {
              text: 'I declare that the materials shipped are not governed by the Washington Convention on International Trade in Endangered Species of Wild Fauna and Flora as well as in the attached list of R / CE 338/1997 and its latest amendments.\n',
              style: 'fieldName',
            },
            { text: 'Decision: ' + declarationText, style: 'content' },
          ],
          margin: [0, 5, 0, 10],
        },
      ],
    }

    const permanentStatement = {
      unbreakable: true,
      stack: [
        {
          text: [
            { text: '9. Permanent Statement\n', style: 'header' },
            {
              text: 'I declare that I understand that the service only includes permanent export, and does not include temporary exports.\n',
              style: 'fieldName',
            },
            { text: 'Decision: ' + declarationText, style: 'content' },
          ],
          margin: [0, 5, 0, 10],
        },
      ],
    }

    const wasteStatement = {
      unbreakable: true,
      stack: [
        {
          text: [
            { text: '10. Waste Statement\n', style: 'header' },
            {
              text: 'I declare that the materials shipped are not include under the Regulation (EC) No 1013/2006 of the European Parliament and of the Council of 14 June 2006 on shipments of waste, and its updates.\n',
              style: 'fieldName',
            },
            { text: 'Decision: ' + declarationText, style: 'content' },
          ],
          margin: [0, 5, 0, 10],
        },
      ],
    }

    const inventoryTableWithHeader = {
      unbreakable: true,
      stack: [
        {
          // Título del bloque de la tabla
          text: '6. Inventory of contents',
          style: 'header',
        },
        {
          // Definición de la tabla (sin cambios, solo como referencia)
          style: 'tableExample',
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                {
                  text: 'Detailed description in English of goods (incl material, brand, size...)',
                  style: 'tableHeader',
                },
                { text: 'Tariff number', style: 'tableHeader' },
                { text: 'Country of origin', style: 'tableHeader' },
                { text: 'Nº items', style: 'tableHeader' },
                { text: 'Item value', style: 'tableHeader' },
                { text: 'Subtotal', style: 'tableHeader' },
              ],
              ...inventoryItems,
              [
                {
                  text: 'Shipping cost',
                  colSpan: 5,
                  style: 'tableHeader',
                },
                {},
                {},
                {},
                {},
                { text: formattedShippingCost, style: 'tableContent' },
              ],
              [
                {
                  text: 'Overall Total',
                  colSpan: 5,
                  style: 'tableHeader',
                },
                {},
                {},
                {},
                {},
                {
                  text: formattedTotalCostIncludingShipping,
                  style: 'tableContent',
                },
              ],
            ],
          },
          layout: 'lightHorizontalLines',
        },
      ],
    }

    // Obtiene los datos necesarios para la sección de firma
    const signatureData = getSignatureFields(data.from, webUserName)

    const signatureSection = {
      unbreakable: true,
      stack: [
        {
          text: '11. Signature',
          style: 'header',
        },
        {
          text: `I, ${signatureData.fullName}, declare in ${
            signatureData.city
          } on ${getFormattedDate()}, that the information in this invoice is true and correct.`,
          style: 'content',
          margin: [0, 5, 0, 10],
        },
        {
          text: 'Sign:',
          style: 'fieldName',
          margin: [0, 5, 0, 5],
        },
        {
          canvas: [
            { type: 'line', x1: 0, y1: 5, x2: 260, y2: 5, lineWidth: 1 },
          ],
          margin: [0, 2, 0, 10],
        },
        {
          text: `Signed by: ${signatureData.fullName}`,
          style: 'content',
          margin: [0, 2, 0, 0],
        },
      ],
      margin: [0, 20, 0, 0],
    }

    // Definición del documento
    const docDefinition = {
      pageSize: 'A4',
      pageOrientation: 'portrait',
      pageMargins: [40, 60, 40, 60],
      content: [
        {
          text: '1. Customs invoice',
          style: 'header',
        },
        {
          text:
            data.customs.senderType === 'BUSINESS_UNION'
              ? [
                  {
                    text: 'Commercial\n',
                    style: 'fieldName',
                  },
                  {
                    text: 'Invoice number of the sale:',
                    style: 'fieldName',
                  },
                  {
                    text: data.customs.senderInfo.bill,
                    bold: false,
                  },
                ]
              : [
                  {
                    text: 'Non-Commercial',
                    style: 'fieldName',
                  },
                ],
          style: 'content',
        },
        {
          text: '2. Sender information',
          style: 'header',
          margin: [0, 20, 0, 0],
        },
        data.customs.senderType === 'BUSINESS_UNION'
          ? {
              columns: [
                {
                  width: '*',
                  text: [
                    {
                      text: 'Company\n',
                      style: 'fieldName',
                    },
                    {
                      text: 'Company name: ',
                      style: 'fieldName',
                    },
                    {
                      text: data.customs.senderInfo.companyName,
                      style: 'content',
                    },
                    {
                      text: '\nVAT number: ',
                      style: 'fieldName',
                    },
                    {
                      text: data.customs.senderInfo.nifIva,
                      style: 'content',
                    },
                  ],
                  style: 'content',
                },
                {
                  width: '*',
                  stack:
                    data.from.addressSource === 'clientCentersList'
                      ? getFormFields(data.from.selectedAddress.shippingAddress)
                      : getFormFields(data.from.address),
                  style: 'content',
                },
              ],
              columnGap: 10,
            }
          : {
              columns: [
                {
                  width: '*',
                  text: [
                    {
                      text: 'Private Person\n',
                      style: 'fieldName',
                    },
                    {
                      text: 'Tax ID: ',
                      style: 'fieldName',
                    },
                    {
                      text: data.customs.senderInfo.dni,
                      style: 'content',
                    },
                  ],
                  style: 'content',
                },
                {
                  width: '*',
                  stack:
                    data.from.addressSource === 'clientCentersList'
                      ? getFormFields(data.from.selectedAddress.shippingAddress)
                      : getFormFields(data.from.address),
                  style: 'content',
                },
              ],
              columnGap: 10,
            },
        {
          text: '3. Receiver information',
          style: 'header',
          margin: [0, 20, 0, 0],
        },
        data.customs.receptorType === 'BUSINESS_UNION'
          ? {
              columns: [
                {
                  width: '*',
                  text: [
                    {
                      text: 'Company\n',
                      style: 'fieldName',
                    },
                    {
                      text: 'Company name: ',
                      style: 'fieldName',
                    },
                    {
                      text: data.customs.receptorInfo.companyName,
                      style: 'content',
                    },
                    {
                      text: '\nVAT number: ',
                      style: 'fieldName',
                    },
                    {
                      text: data.customs.receptorInfo.nifIva,
                      style: 'content',
                    },
                  ],
                  style: 'content',
                },
                {
                  width: '*',
                  stack: getFormFields(data.to.address),
                  style: 'content',
                },
              ],
              columnGap: 10,
            }
          : {
              columns: [
                {
                  width: '*',
                  text: [
                    {
                      text: 'Private Person\n',
                      style: 'fieldName',
                    },
                    {
                      text: 'Tax ID: ',
                      style: 'fieldName',
                    },
                    {
                      text: data.customs.receptorInfo.dni,
                      style: 'content',
                    },
                  ],
                  style: 'content',
                },
                {
                  width: '*',
                  stack: getFormFields(data.to.address),
                  style: 'content',
                },
              ],
              columnGap: 10,
            },
        {
          columns: [
            {
              width: '*',
              stack: [
                {
                  text: '4. Reason for export',
                  style: 'header',
                },
                {
                  text: getExportReason(data.customs.senderInfo.reason),
                  style: 'content',
                },
              ],
            },
            {
              width: '*',
              stack: [
                {
                  text: '5. Shipment details',
                  style: 'header',
                },
                {
                  text: 'Number of parcels: ',
                  style: 'fieldName',
                },
                {
                  text: data.shipment.packages.length,
                  style: 'content',
                },
                {
                  text: 'Total weight: ',
                  style: 'fieldName',
                },
                {
                  text: totalWeight + ' kg',
                  style: 'content',
                },
                {
                  text: 'Shipping cost (€): ',
                  style: 'fieldName',
                },
                {
                  text: data.shipment.serviceType.basePrice,
                  style: 'content',
                },
                {
                  text: 'Terms of delivery (Incoterms): DAP',
                  style: 'content',
                },
              ],
            },
          ],
          columnGap: 10,
        },
      ],
      styles,
      footer: function (currentPage, pageCount) {
        return {
          margin: [40, 0, 40, 0],
          text: [
            {
              text: 'Página ' + currentPage.toString() + ' de ' + pageCount,
              alignment: 'center',
              style: 'content',
            },
          ],
        }
      },
    }
    docDefinition.content.push(inventoryTableWithHeader)
    docDefinition.content.push(dualUseDeclaration)
    docDefinition.content.push(citesDeclaration)
    docDefinition.content.push(permanentStatement)
    docDefinition.content.push(wasteStatement)
    docDefinition.content.push(signatureSection)
    pdfMake.createPdf(docDefinition).getBuffer(buffer => {
      const blob = new Blob([buffer], { type: 'application/pdf' })
      const file = new File([blob], 'documento.pdf', {
        type: 'application/pdf',
      })
      resolve(file)
    })
  })
}

// Función para obtener campos de formulario
function getFormFields(data) {
  return [
    {
      columns: [
        { text: 'Full Name: ', style: 'fieldName', width: 'auto' },
        { text: data.name, style: 'content', width: '*' },
      ],
    },
    {
      columns: [
        { text: 'Phone: ', style: 'fieldName', width: 'auto' },
        { text: data.phone, style: 'content', width: '*' },
      ],
    },
    {
      columns: [
        { text: 'Address: ', style: 'fieldName', width: 'auto' },
        { text: data.address, style: 'content', width: '*' },
      ],
    },
    {
      columns: [
        { text: 'Postal Code: ', style: 'fieldName', width: 'auto' },
        { text: data.postalCode, style: 'content', width: '*' },
      ],
    },
    {
      columns: [
        { text: 'City: ', style: 'fieldName', width: 'auto' },
        { text: data.city, style: 'content', width: '*' },
      ],
    },
    {
      columns: [
        { text: 'Country: ', style: 'fieldName', width: 'auto' },
        { text: data.country, style: 'content', width: '*' },
      ],
    },
  ]
}

function getExportReason(reasonId) {
  return CUSTOM_SELECT_REASON_ENGLISH[reasonId] || 'Unknown Reason'
}

function getFormattedDate() {
  const today = new Date()
  const day = today.getDate().toString().padStart(2, '0')
  const month = (today.getMonth() + 1).toString().padStart(2, '0')
  const year = today.getFullYear()

  return `${day}/${month}/${year}`
}

function getSignatureFields(fromData, webUserName) {
  if (fromData.addressSource === 'clientCentersList') {
    const address = fromData.selectedAddress.shippingAddress
    return {
      fullName: webUserName,
      city: address.city,
    }
  } else {
    const address = fromData.address
    return {
      fullName: webUserName,
      city: address.city,
    }
  }
}

export { generateReportPDF }
